import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Pagination,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveMessage,
  EngagementApiError,
  estimateAudience,
  getEngagementSettings,
  getMessage,
  getMessages,
  getTemplates,
  type MessageSourceFilter,
  previewMessage,
  sendMessage,
  sendTestMessage,
} from 'API/engagement';
import { useUserData } from 'hooks/use-user-data';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CHANNELS, type AudienceEstimate, type Channel, type EngagementMessage, type MessagePreview, type SendResult } from 'types/engagement.types';
import { fmtDate, fmtInt } from '../../shared/format';
import { QueryState, SectionCard } from '../../shared/ui';
import AudienceEditor from '../audience-editor';
import {
  buildComposeRequest,
  contentFingerprint,
  initialComposeState,
  requiresApproval,
  requiresTest,
  sendBlocker,
  TEST_REQUIRED_ABOVE,
  type ComposeState,
  type SendBlocker,
  type TestRecord,
} from '../compose-logic';
import { deriveSendEnabled } from '../switches';

// ------------------------------------------------------------------ Send controls (presentational)

interface SendControlsProps {
  estimate?: AudienceEstimate;
  blocker: SendBlocker;
  needsTest: boolean;
  needsApproval: boolean;
  testDone: boolean;
  testing: boolean;
  sending: boolean;
  canTest: boolean;
  onTest(): void;
  onSend(): void;
}

/** "Test an mich" + "Senden". Senden stays disabled until a matching test went out (> 20 recipients). */
export const SendControls: React.FC<SendControlsProps> = ({
  estimate,
  blocker,
  needsTest,
  needsApproval,
  testDone,
  testing,
  sending,
  canTest,
  onTest,
  onSend,
}) => {
  const { t } = useTranslation();
  const count = estimate?.targeted ?? 0;
  return (
    <Stack gap={1}>
      <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
        <Button variant={'outlined'} onClick={onTest} disabled={!canTest || testing} data-testid={'compose-test'}>
          {testing ? t('engComposeTesting') : testDone ? t('engComposeTestAgain') : t('engComposeTest')}
        </Button>
        <Button variant={'contained'} onClick={onSend} disabled={blocker !== null || sending} data-testid={'compose-send'}>
          {needsApproval ? t('engComposeRequestApproval', { count }) : t('engComposeSend', { count })}
        </Button>
      </Stack>
      {blocker === 'needs_test' ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }} data-testid={'compose-hint'}>
          {t('engComposeHintNeedsTest', { limit: TEST_REQUIRED_ABOVE })}
        </Typography>
      ) : blocker ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }} data-testid={'compose-hint'}>
          {t(`engComposeBlocker_${blocker}`)}
        </Typography>
      ) : needsTest && testDone ? (
        <Typography sx={{ fontSize: 12, color: 'success.main' }}>{t('engComposeTestOk')}</Typography>
      ) : null}
      {needsApproval ? <Typography sx={{ fontSize: 12, color: 'warning.dark' }}>{t('engComposeApprovalHint')}</Typography> : null}
    </Stack>
  );
};

// ------------------------------------------------------------------ Preview

export const PreviewPanel: React.FC<{ preview?: MessagePreview; userLabel?: string }> = ({ preview, userLabel }) => {
  const { t } = useTranslation();
  if (!preview) return <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engPreviewEmpty')}</Typography>;
  return (
    <Stack gap={1.5}>
      {userLabel ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('engPreviewFor', { user: userLabel })}</Typography> : null}
      {preview.missing?.length ? (
        <Alert severity={'warning'}>{t('engPreviewMissing', { vars: preview.missing.join(', ') })}</Alert>
      ) : null}
      {preview.notification ? (
        <Box sx={{ border: '1px solid', borderColor: 'greys.lighter', borderRadius: 2, p: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>{t('engChannel_notification')}</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{preview.notification.title}</Typography>
          <Typography sx={{ fontSize: 13, mt: 0.5 }}>{preview.notification.body}</Typography>
          {preview.notification.ctaLabel ? (
            <Button size={'small'} variant={'contained'} sx={{ mt: 1 }} disabled>
              {preview.notification.ctaLabel}
            </Button>
          ) : null}
        </Box>
      ) : null}
      {preview.email ? (
        <Box sx={{ border: '1px solid', borderColor: 'greys.lighter', borderRadius: 2, p: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>{t('engPreviewEmailSubject')}</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{preview.email.subject}</Typography>
          {/* Sandboxed: the HTML comes from our API, but it is still rendered content. */}
          <iframe
            title={'email-preview'}
            sandbox={''}
            srcDoc={preview.email.html}
            style={{ width: '100%', minHeight: 260, border: '1px solid #EEE', borderRadius: 6, marginTop: 8, background: '#FFF' }}
          />
        </Box>
      ) : null}
      {preview.banner ? (
        <Box sx={{ border: '1px solid', borderColor: 'greys.lighter', borderRadius: 2, p: 1.5, bgcolor: '#F5F5FB' }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>{t('engChannel_banner')}</Typography>
          <Typography sx={{ fontSize: 13 }}>{preview.banner.body}</Typography>
        </Box>
      ) : null}
    </Stack>
  );
};

// ------------------------------------------------------------------ Compose

const errorText = (e: unknown, t: (k: string, o?: Record<string, unknown>) => string): string => {
  const err = e as EngagementApiError;
  if (err?.status === 409) return t('engComposeError409');
  return `${t('engComposeErrorGeneric')}${err?.message ? ` (${err.message})` : ''}`;
};

/** `sendEnabled === false`: the hub records the send but delivers nothing to customers. */
const ComposePanel: React.FC<{ sendEnabled?: boolean | null }> = ({ sendEnabled }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [s, setS] = useState<ComposeState>(initialComposeState);
  const [test, setTest] = useState<TestRecord | null>(null);
  const [preview, setPreview] = useState<MessagePreview | undefined>();
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['engagement', 'templates'], queryFn: getTemplates });
  const estimate = useQuery({
    queryKey: ['engagement', 'estimate', s.audience, s.topicClass, s.channels],
    queryFn: () => estimateAudience(s.audience, s.topicClass, s.channels),
  });

  const selectedTemplate = templates.data?.find((tpl) => tpl.key === s.templateKey);
  const manualVars = (selectedTemplate?.variables ?? []).filter((v) => v.source === 'manual');

  const update = (patch: Partial<ComposeState>) => {
    setS((prev) => ({ ...prev, ...patch }));
    setResult(null);
    setError(null);
  };

  const previewReq = () => {
    const req = buildComposeRequest(s, test);
    return { templateKey: req.templateKey, content: req.content, channels: req.channels, variableValues: req.variableValues };
  };

  const previewMutation = useMutation({
    mutationFn: () => previewMessage(previewReq()),
    onSuccess: (p) => setPreview(p),
    onError: (e) => setError(errorText(e, t)),
  });

  const testMutation = useMutation({
    mutationFn: () => {
      const req = buildComposeRequest(s, null);
      return sendTestMessage({
        topic: req.topic,
        topicClass: req.topicClass,
        channels: req.channels,
        templateKey: req.templateKey,
        content: req.content,
        variableValues: req.variableValues,
        delivery: req.delivery,
      });
    },
    onSuccess: (res) => setTest({ testId: res.testId, fingerprint: contentFingerprint(s) }),
    onError: (e) => setError(errorText(e, t)),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendMessage(buildComposeRequest(s, test)),
    onSuccess: (res) => {
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ['engagement', 'messages'] });
    },
    onError: (e) => setError(errorText(e, t)),
  });

  const blocker = sendBlocker(s, estimate.data, test);
  const needsTest = requiresTest(estimate.data);
  const testDone = !!test && test.fingerprint === contentFingerprint(s);
  const est = estimate.data;
  const excludedTotal = est ? Object.values(est.excluded ?? {}).reduce((a, b) => a + b, 0) : 0;

  const toggleChannel = (c: Channel) =>
    update({ channels: s.channels.includes(c) ? s.channels.filter((x) => x !== c) : [...s.channels, c] });

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <Box sx={{ flex: 3, minWidth: 0 }}>
        <SectionCard title={t('engComposeTitle')}>
          <Stack gap={2}>
            <RadioGroup row value={s.mode} onChange={(e) => update({ mode: e.target.value as ComposeState['mode'] })}>
              <FormControlLabel value={'template'} control={<Radio size={'small'} />} label={t('engComposeModeTemplate')} />
              <FormControlLabel value={'free'} control={<Radio size={'small'} />} label={t('engComposeModeFree')} />
            </RadioGroup>

            {s.mode === 'template' ? (
              <>
                <TextField
                  select
                  size={'small'}
                  label={t('engComposeTemplate')}
                  value={s.templateKey}
                  onChange={(e) => {
                    const tpl = templates.data?.find((x) => x.key === e.target.value);
                    update({ templateKey: e.target.value, topicClass: tpl?.topicClass ?? s.topicClass, variableValues: {} });
                  }}
                  helperText={templates.error ? t('engErrorLoad') : selectedTemplate?.recipientBenefit}
                >
                  {(templates.data ?? [])
                    .filter((tpl) => tpl.active)
                    .map((tpl) => (
                      <MenuItem key={tpl.key} value={tpl.key}>
                        {tpl.name}
                      </MenuItem>
                    ))}
                </TextField>
                {manualVars.map((v) => (
                  <TextField
                    key={v.name}
                    size={'small'}
                    label={`{{${v.name}}}`}
                    helperText={v.instruction}
                    value={s.variableValues[v.name] ?? ''}
                    onChange={(e) => update({ variableValues: { ...s.variableValues, [v.name]: e.target.value } })}
                  />
                ))}
              </>
            ) : (
              <>
                <TextField size={'small'} label={t('engComposeTitleField')} value={s.content.title} onChange={(e) => update({ content: { ...s.content, title: e.target.value } })} />
                <TextField
                  size={'small'}
                  multiline
                  minRows={3}
                  label={t('engComposeBody')}
                  value={s.content.body}
                  onChange={(e) => update({ content: { ...s.content, body: e.target.value } })}
                />
                <Stack direction={'row'} gap={1.5}>
                  <TextField
                    size={'small'}
                    label={t('engComposeCtaLabel')}
                    value={s.content.ctaLabel ?? ''}
                    onChange={(e) => update({ content: { ...s.content, ctaLabel: e.target.value } })}
                  />
                  <TextField
                    size={'small'}
                    fullWidth
                    label={t('engComposeCtaUrl')}
                    placeholder={'/recruiting-booster/co-pilot/inbox'}
                    value={s.content.ctaUrl ?? ''}
                    onChange={(e) => update({ content: { ...s.content, ctaUrl: e.target.value } })}
                  />
                </Stack>
                <TextField
                  select
                  size={'small'}
                  label={t('engComposeTopicClass')}
                  value={s.topicClass}
                  onChange={(e) => update({ topicClass: e.target.value as ComposeState['topicClass'] })}
                >
                  {(['product', 'news', 'offer', 'transactional'] as const).map((c) => (
                    <MenuItem key={c} value={c}>
                      {t(`engTopicClass_${c}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}

            <TextField size={'small'} label={t('engComposeTopic')} helperText={t('engComposeTopicHelp')} value={s.topic} onChange={(e) => update({ topic: e.target.value })} />

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{t('engComposeChannels')}</Typography>
              <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                {CHANNELS.map((c) => (
                  <FormControlLabel
                    key={c}
                    control={<Checkbox size={'small'} checked={s.channels.includes(c)} onChange={() => toggleChannel(c)} />}
                    label={t(`engChannel_${c}`)}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>{t('engComposeAudience')}</Typography>
              <AudienceEditor value={s.audience} onChange={(a) => update({ audience: a })} />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{t('engComposeDelivery')}</Typography>
              <RadioGroup row value={s.delivery} onChange={(e) => update({ delivery: e.target.value as ComposeState['delivery'] })}>
                <FormControlLabel value={'window'} control={<Radio size={'small'} />} label={t('engComposeDeliveryWindow')} />
                <FormControlLabel value={'immediate'} control={<Radio size={'small'} />} label={t('engComposeDeliveryImmediate')} />
              </RadioGroup>
            </Box>

            <Box data-testid={'compose-estimate'}>
              {estimate.isLoading ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engEstimateLoading')}</Typography>
              ) : estimate.error ? (
                <Alert severity={'error'}>{t('engEstimateFailed')}</Alert>
              ) : est ? (
                <Typography sx={{ fontSize: 13 }}>
                  {t('engEstimateText', { targeted: fmtInt(i18n.language)(est.targeted), withEmail: fmtInt(i18n.language)(est.withEmail) })}
                  {excludedTotal > 0
                    ? ` · ${Object.entries(est.excluded)
                        .filter(([, n]) => n > 0)
                        .map(([reason, n]) => t('engEstimateExcluded', { count: n, reason: t(`engSkip_${reason}`, { defaultValue: reason }) }))
                        .join(' · ')}`
                    : ''}
                </Typography>
              ) : null}
            </Box>

            <Stack direction={'row'} gap={1.5}>
              <Button variant={'text'} onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
                {t('engComposePreview')}
              </Button>
            </Stack>

            <SendControls
              estimate={est}
              blocker={blocker}
              needsTest={needsTest}
              needsApproval={requiresApproval(s.audience, est)}
              testDone={testDone}
              testing={testMutation.isPending}
              sending={sendMutation.isPending}
              canTest={blocker !== 'no_content' && blocker !== 'no_channel'}
              onTest={() => testMutation.mutate()}
              onSend={() => sendMutation.mutate()}
            />

            {error ? <Alert severity={'error'}>{error}</Alert> : null}
            {result ? (
              <Alert severity={result.status === 'awaiting_approval' ? 'warning' : 'success'} data-testid={'compose-result'}>
                {result.status === 'completed' && sendEnabled === false
                  ? t('engSendResult_completed_disabled')
                  : t(`engSendResult_${result.status}`, { defaultValue: result.status })}
              </Alert>
            ) : null}
          </Stack>
        </SectionCard>
      </Box>
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <SectionCard title={t('engPreviewTitle')}>
          <PreviewPanel preview={preview} userLabel={preview?.recipient?.name || undefined} />
          {s.mode === 'template' && selectedTemplate?.variables?.some((v) => v.source === 'deterministic') ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>{t('engPreviewVarsNote')}</Typography>
          ) : null}
        </SectionCard>
      </Box>
    </Stack>
  );
};

// ------------------------------------------------------------------ Outbox

const sumSkipped = (m: EngagementMessage) => Object.values(m.aggregate?.skipped ?? {}).reduce((a, b) => a + b, 0);

const sourceLabel = (m: EngagementMessage, t: (k: string, o?: Record<string, unknown>) => string) => {
  const src = m.source;
  if (!src) return '—';
  if (src.type === 'automation') return t('engSourceAutomation', { key: src.refId ?? '' });
  if (src.type === 'system') return t('engSourceSystem');
  if (src.actor) return t(src.actor.via === 'mcp' ? 'engSourceViaMcp' : 'engSourceViaUi', { name: src.actor.name ?? src.actor.userId });
  return t(`engSourceType_${src.type}`, { defaultValue: src.type });
};

const MessageDetailDialog: React.FC<{ id: string | null; onClose(): void }> = ({ id, onClose }) => {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ['engagement', 'message', id], queryFn: () => getMessage(id as string), enabled: !!id });
  return (
    <Dialog open={!!id} onClose={onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>{q.data?.topic ?? t('engMessageDetail')}</DialogTitle>
      <DialogContent>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {q.data ? (
            <Stack gap={2}>
              {q.data.killSwitch ? (
                <Alert severity={'error'}>{t('engKillSwitch', { reason: q.data.killSwitch.reason, at: fmtDate(q.data.killSwitch.triggeredAt, i18n.language, true) })}</Alert>
              ) : null}
              <Table size={'small'}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('engColRecipient')}</TableCell>
                    <TableCell>{t('engColChannel')}</TableCell>
                    <TableCell>{t('engColStatus')}</TableCell>
                    <TableCell>{t('engColSentAt')}</TableCell>
                    <TableCell>{t('engColOpened')}</TableCell>
                    <TableCell>{t('engColReturned')}</TableCell>
                    <TableCell>{t('engColCompleted')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(q.data.deliveries ?? []).map((d, i) => (
                    <TableRow key={d._id ?? i}>
                      <TableCell>{d.userName ?? d.email ?? d.userId}</TableCell>
                      <TableCell>{t(`engChannel_${d.channel}`)}</TableCell>
                      <TableCell>
                        {t(`engDeliveryStatus_${d.status}`, { defaultValue: d.status })}
                        {d.reason ? ` (${d.reason})` : ''}
                      </TableCell>
                      <TableCell>{fmtDate(d.sentAt, i18n.language, true)}</TableCell>
                      <TableCell>{fmtDate(d.openedAt, i18n.language, true)}</TableCell>
                      <TableCell>{fmtDate(d.returnedAt, i18n.language, true)}</TableCell>
                      <TableCell>{fmtDate(d.completedAt, i18n.language, true)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(q.data.artifacts ?? []).length ? (
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>{t('engArtifacts')}</Typography>
                  {(q.data.artifacts ?? []).slice(0, 5).map((a, i) => (
                    <Box key={a.deliveryId ?? i} sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: 12 }}>
                        {a.to} · {a.subject} · {fmtDate(a.sentAt, i18n.language, true)}
                      </Typography>
                      {a.html ? (
                        <iframe title={`artifact-${i}`} sandbox={''} srcDoc={a.html} style={{ width: '100%', minHeight: 200, border: '1px solid #EEE' }} />
                      ) : (
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{a.text}</pre>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </QueryState>
      </DialogContent>
    </Dialog>
  );
};

interface OutboxProps {
  messages: EngagementMessage[];
  myUserId: string;
  approving?: string | null;
  onApprove(id: string): void;
  onOpen(id: string): void;
}

export const Outbox: React.FC<OutboxProps> = ({
  messages,
  myUserId,
  onApprove,
  approving,
  onOpen,
}) => {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size={'small'}>
        <TableHead>
          <TableRow>
            <TableCell>{t('engColDate')}</TableCell>
            <TableCell>{t('engColTopic')}</TableCell>
            <TableCell>{t('engColFrom')}</TableCell>
            <TableCell>{t('engColChannels')}</TableCell>
            <TableCell align={'right'}>{t('engColTargeted')}</TableCell>
            <TableCell align={'right'}>{t('engColDelivered')}</TableCell>
            <TableCell align={'right'}>{t('engColOpened')}</TableCell>
            <TableCell align={'right'}>{t('engColReturned')}</TableCell>
            <TableCell align={'right'}>{t('engColCompleted')}</TableCell>
            <TableCell align={'right'}>{t('engColSkipped')}</TableCell>
            <TableCell>{t('engColStatus')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {messages.map((m) => {
            const a = m.aggregate;
            const awaiting = m.status === 'awaiting_approval';
            // The requester of an approval is the sender (source.actor); the API refuses their own approval.
            const ownRequest = !!m.source?.actor?.userId && m.source.actor.userId === myUserId;
            return (
              <TableRow key={m._id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(m._id)}>
                <TableCell>{fmtDate(m.scheduledFor ?? m.createdAt, i18n.language)}</TableCell>
                <TableCell>{m.topic}</TableCell>
                <TableCell>{sourceLabel(m, t)}</TableCell>
                <TableCell>{m.channels.map((c) => t(`engChannel_${c}`)).join(', ')}</TableCell>
                <TableCell align={'right'}>{a?.targeted ?? '—'}</TableCell>
                <TableCell align={'right'}>{a?.delivered ?? '—'}</TableCell>
                <TableCell align={'right'}>{a?.opened ?? '—'}</TableCell>
                <TableCell align={'right'}>{a?.returned ?? '—'}</TableCell>
                <TableCell align={'right'}>{a?.completed ?? '—'}</TableCell>
                <TableCell align={'right'}>
                  {a?.skipped && sumSkipped(m) > 0 ? (
                    <Tooltip
                      title={Object.entries(a.skipped)
                        .map(([r, n]) => `${t(`engSkip_${r}`, { defaultValue: r })}: ${n}`)
                        .join(', ')}
                    >
                      <span>{sumSkipped(m)}</span>
                    </Tooltip>
                  ) : (
                    0
                  )}
                </TableCell>
                <TableCell onClick={(e) => awaiting && e.stopPropagation()}>
                  {awaiting ? (
                    <Stack direction={'row'} gap={1} alignItems={'center'}>
                      <Chip size={'small'} color={'warning'} label={t('engStatus_awaiting_approval')} />
                      <Tooltip title={ownRequest ? t('engApproveOwnHint') : ''}>
                        <span>
                          <Button
                            size={'small'}
                            variant={'outlined'}
                            disabled={ownRequest || approving === m._id}
                            onClick={() => onApprove(m._id)}
                            data-testid={'approve-button'}
                          >
                            {t('engApprove')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Chip size={'small'} label={t(`engStatus_${m.status}`, { defaultValue: m.status })} color={m.status === 'failed' ? 'error' : 'default'} />
                  )}
                  {m.killSwitch ? <Chip size={'small'} color={'error'} label={t('engKillSwitchShort')} sx={{ ml: 1 }} /> : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export const SendDisabledBanner: React.FC<{ kind: 'send' | 'automations'; derived?: boolean }> = ({ kind, derived }) => {
  const { t } = useTranslation();
  return (
    <Alert severity={'warning'} data-testid={`switch-banner-${kind}`}>
      {kind === 'send' ? t('engBannerSendOff') : t('engBannerAutomationsOff')}
      {derived ? ` ${t('engBannerDerived')}` : ''}
    </Alert>
  );
};

const SOURCE_FILTERS: Array<MessageSourceFilter | ''> = ['', 'automation', 'system', 'manual', 'test'];

/** Automatik / System (the customer mails the code sends: reset, 2FA, daily report …) / Admin. */
export const OutboxSourceFilter: React.FC<{ value: MessageSourceFilter | ''; onChange(v: MessageSourceFilter | ''): void }> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <TextField
      select
      size={'small'}
      label={t('engOutboxSource')}
      value={value}
      onChange={(e) => onChange(e.target.value as MessageSourceFilter | '')}
      sx={{ mb: 1.5, minWidth: 220 }}
      inputProps={{ 'data-testid': 'outbox-source-filter' }}
    >
      {SOURCE_FILTERS.map((f) => (
        <MenuItem key={f || 'all'} value={f}>
          {t(`engOutboxSource_${f || 'all'}`)}
        </MenuItem>
      ))}
    </TextField>
  );
};

const MessagesTab: React.FC = () => {
  const { t } = useTranslation();
  const { id: myUserId } = useUserData();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [source, setSource] = useState<MessageSourceFilter | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);

  const messages = useQuery({
    queryKey: ['engagement', 'messages', page, source],
    queryFn: () => getMessages(page, source || undefined),
  });
  const settings = useQuery({ queryKey: ['engagement', 'settings'], queryFn: getEngagementSettings });
  const approve = useMutation({
    mutationFn: approveMessage,
    onSuccess: () => {
      setApproveError(null);
      queryClient.invalidateQueries({ queryKey: ['engagement', 'messages'] });
    },
    onError: (e) => setApproveError((e as Error).message),
  });

  const items = useMemo(() => messages.data?.items ?? [], [messages.data]);
  const sendEnabled = deriveSendEnabled(settings.data, items);
  const pages = messages.data?.total ? Math.max(1, Math.ceil(messages.data.total / Math.max(1, items.length || 20))) : 1;

  return (
    <Stack gap={2.5}>
      {sendEnabled === false ? <SendDisabledBanner kind={'send'} derived={typeof settings.data?.flags?.sendEnabled !== 'boolean'} /> : null}
      <ComposePanel sendEnabled={sendEnabled} />
      <SectionCard title={t('engOutboxTitle')}>
        {approveError ? (
          <Alert severity={'error'} sx={{ mb: 1 }}>
            {approveError}
          </Alert>
        ) : null}
        <OutboxSourceFilter
          value={source}
          onChange={(v) => {
            setSource(v);
            setPage(1);
          }}
        />
        <QueryState isLoading={messages.isLoading} error={messages.error} isEmpty={items.length === 0} emptyText={t('engOutboxEmpty')}>
          <Outbox
            messages={items}
            myUserId={myUserId}
            onApprove={(id) => approve.mutate(id)}
            approving={approve.isPending ? approve.variables : null}
            onOpen={setOpenId}
          />
          {pages > 1 ? <Pagination sx={{ mt: 1.5 }} count={pages} page={page} onChange={(_, p) => setPage(p)} /> : null}
        </QueryState>
      </SectionCard>
      <MessageDetailDialog id={openId} onClose={() => setOpenId(null)} />
    </Stack>
  );
};

export default MessagesTab;
